/**
 * Display formatting. Dates cross the wire as ISO-8601 strings (see
 * `isoDateTimeSchema` in `@wend/shared`) and are rendered in the viewer's own
 * locale and timezone.
 */

const DATE = new Intl.DateTimeFormat(undefined, {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const DATE_TIME = new Intl.DateTimeFormat(undefined, {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const NUMBER = new Intl.NumberFormat();

/** Em dash for anything unparseable, so a bad value never renders as `Invalid Date`. */
const ABSENT = '—';

function parse(iso: string): Date | null {
  const time = Date.parse(iso);
  return Number.isNaN(time) ? null : new Date(time);
}

export function formatDate(iso: string): string {
  const date = parse(iso);
  return date === null ? ABSENT : DATE.format(date);
}

export function formatDateTime(iso: string): string {
  const date = parse(iso);
  return date === null ? ABSENT : DATE_TIME.format(date);
}

/** Row counts are grouped — a 200,000-row file is unreadable without it. */
export function formatCount(value: number): string {
  return NUMBER.format(value);
}

/** `1 row` / `4 rows`, so a count never reads as "1 rows". */
export function pluralise(value: number, singular: string, plural = `${singular}s`): string {
  return `${formatCount(value)} ${value === 1 ? singular : plural}`;
}

const RUPEES = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 });
const RUPEES_WHOLE = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });

/**
 * Integer paise as rupees, Indian grouping: `₹1,20,000`, `₹99.50`. The division
 * happens here, at display, so the figure is an exact integer until the last line.
 */
export function formatRupees(minor: number): string {
  return minor % 100 === 0 ? RUPEES_WHOLE.format(minor / 100) : RUPEES.format(minor / 100);
}

const CALENDAR_DAY = new Intl.DateTimeFormat(undefined, { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });

/**
 * A calendar day (`YYYY-MM-DD`) as the day it names. Formatted in UTC because
 * the string IS a day, not an instant: rendering it in the viewer's timezone
 * would show 3 October for "2026-10-04" to anyone west of Greenwich.
 */
export function formatCalendarDay(day: string): string {
  const time = Date.parse(`${day}T00:00:00Z`);
  return Number.isNaN(time) ? ABSENT : CALENDAR_DAY.format(new Date(time));
}
